/**
 * Tansy · carrossel simples (rolagem lateral com botões anterior/próximo).
 * Usado pelos Depoimentos (e, no fim do arquivo, a correção de acessibilidade dos carrosséis do Horizon).
 * Os botões só aparecem quando há conteúdo para rolar;
 * a rolagem também funciona por toque e pelo teclado (a trilha recebe foco).
 */
class VeluneCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');
    this.addEventListener('click', this.onClick);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.track) return;
    const button = target.closest('[data-carousel-prev], [data-carousel-next]');
    if (!button) return;
    const direction = button.hasAttribute('data-carousel-next') ? 1 : -1;
    const first = this.track.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    const step = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollBy({ left: direction * step, behavior: reduce ? 'auto' : 'smooth' });
  };

  onScroll = () => this.update();

  update() {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth - 2;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
    if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    this.toggleAttribute('data-scrollable', max > 0);
  }
}

if (!customElements.get('velune-carousel')) {
  customElements.define('velune-carousel', VeluneCarousel);
}

/**
 * Tansy · acessibilidade dos carrosséis do Horizon (sem mexer nos arquivos do Horizon).
 *
 * 1. Slides ocultos: o Horizon marca os slides fora da tela com aria-hidden="true", mas os links e botões
 *    dentro deles continuavam na ordem do Tab (falha grave "aria-hidden-focus"). Enquanto o slide está
 *    oculto, os links e botões dele saem do Tab; continuam clicáveis e voltam ao normal quando o slide aparece.
 *
 * 2. Carrosséis só de imagens (ex.: galeria do produto no celular): a trilha rolável vem com tabindex="-1";
 *    sem nenhum link ou botão dentro, quem usa teclado não conseguia rolar (falha grave
 *    "scrollable-region-focusable"). Nesses casos a trilha passa a receber foco (as setas rolam) e ganha
 *    um nome para leitores de tela. Só vale para trilhas que a pessoa consegue rolar e que não estão ocultas.
 */
const VELUNE_SLIDE = 'slideshow-slide';
const VELUNE_SCROLLER = 'slideshow-slides';
const VELUNE_FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
const VELUNE_TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
let veluneScrollerTimer = 0;

function veluneSyncSlide(slide) {
  const hidden = slide.getAttribute('aria-hidden') === 'true';
  for (const element of slide.querySelectorAll(VELUNE_FOCUSABLE)) {
    // As trilhas roláveis são cuidadas pela parte 2
    if (element.matches(VELUNE_SCROLLER)) continue;
    if (hidden) {
      if (!element.hasAttribute('data-velune-tabindex')) {
        element.setAttribute('data-velune-tabindex', element.getAttribute('tabindex') ?? '');
        element.setAttribute('tabindex', '-1');
      }
    } else if (element.hasAttribute('data-velune-tabindex')) {
      const original = element.getAttribute('data-velune-tabindex');
      if (original === '') element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', original);
      element.removeAttribute('data-velune-tabindex');
    }
  }
  veluneSyncScrollersSoon();
}

function veluneSyncSlides(root) {
  if (root instanceof Element && root.matches(VELUNE_SLIDE)) veluneSyncSlide(root);
  root.querySelectorAll?.(`${VELUNE_SLIDE}[aria-hidden]`).forEach(veluneSyncSlide);
}

function veluneHasTabbable(scroller) {
  return [...scroller.querySelectorAll(VELUNE_TABBABLE)].some(
    (element) => element.getAttribute('tabindex') !== '-1' && element.getClientRects().length > 0
  );
}

// Só conta como rolável o que a pessoa consegue rolar (overflow auto ou scroll), como faz o axe.
function veluneIsUserScrollable(scroller) {
  const style = getComputedStyle(scroller);
  const canScrollX = /(auto|scroll)/.test(style.overflowX) && scroller.scrollWidth > scroller.clientWidth + 1;
  const canScrollY = /(auto|scroll)/.test(style.overflowY) && scroller.scrollHeight > scroller.clientHeight + 1;
  return canScrollX || canScrollY;
}

function veluneSyncScroller(scroller) {
  const hiddenByAncestor = Boolean(scroller.parentElement?.closest('[aria-hidden="true"], [inert]'));
  const needsFocus = !hiddenByAncestor && veluneIsUserScrollable(scroller) && !veluneHasTabbable(scroller);
  const managed = scroller.hasAttribute('data-velune-scroller');
  if (needsFocus && !managed) {
    scroller.setAttribute('data-velune-scroller', scroller.getAttribute('tabindex') ?? '');
    scroller.setAttribute('tabindex', '0');
    if (!scroller.hasAttribute('role')) {
      scroller.setAttribute('role', 'region');
      scroller.setAttribute('data-velune-role', '');
    }
    if (!scroller.hasAttribute('aria-label')) {
      const owner = scroller.closest('[aria-label]');
      scroller.setAttribute('aria-label', owner?.getAttribute('aria-label') || 'Image gallery');
      scroller.setAttribute('data-velune-label', '');
    }
  } else if (!needsFocus && managed) {
    const original = scroller.getAttribute('data-velune-scroller');
    if (original === '') scroller.removeAttribute('tabindex');
    else scroller.setAttribute('tabindex', original);
    scroller.removeAttribute('data-velune-scroller');
    if (scroller.hasAttribute('data-velune-role')) {
      scroller.removeAttribute('role');
      scroller.removeAttribute('data-velune-role');
    }
    if (scroller.hasAttribute('data-velune-label')) {
      scroller.removeAttribute('aria-label');
      scroller.removeAttribute('data-velune-label');
    }
  }
}

function veluneSyncScrollersSoon() {
  window.clearTimeout(veluneScrollerTimer);
  veluneScrollerTimer = window.setTimeout(() => {
    document.querySelectorAll(VELUNE_SCROLLER).forEach(veluneSyncScroller);
  }, 150);
}

if (!window.veluneCarouselA11y) {
  window.veluneCarouselA11y = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target instanceof Element && mutation.target.matches(VELUNE_SLIDE)) veluneSyncSlide(mutation.target);
      } else {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          veluneSyncSlides(node);
          if (node.matches(VELUNE_SCROLLER) || node.querySelector(VELUNE_SCROLLER)) veluneSyncScrollersSoon();
        }
      }
    }
  });
  window.veluneCarouselA11y.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
  window.addEventListener('resize', veluneSyncScrollersSoon, { passive: true });
  window.addEventListener('load', veluneSyncScrollersSoon);
  veluneSyncSlides(document);
  veluneSyncScrollersSoon();
}
